import { Link, useNavigate, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ManagerAuthorization, ServiceType } from '@common/enums';
import { tryParseInt } from '@common/utils/tryParseInt';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    Header,
    HeaderSearchBox,
    HeaderSearchSelectBox,
    LinkButton,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '@frontend/components/elements/common';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import type { IService } from '../../interfaces/service';
import { ListPager } from '../../elements/ListPager';
import { NullableBoolean } from '../../elements/NullableBoolean';
import { FullWidthTemplate } from '../../templates/FullWidthTemplate';

const serviceSelectOptions: { text: string; value: string }[] = [
    { value: '', text: translateKey('common.selectItem', translateKey('service.serviceType')) },
    ...[
        ServiceType.counseling,
        ServiceType.consultation,
        ServiceType.examination,
        ServiceType.referral,
        ServiceType.referralSmall,
    ].map((serviceType) => ({ value: serviceType, text: translateKey(`serviceTypeEnum.${serviceType}`) })),
];

export function ServiceList() {
    assertHasAuthorization(ManagerAuthorization.servicesRead);
    const hasServiceAddEditAuthorization = hasOneAuthorization(ManagerAuthorization.serviceAddEdit);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const page = searchParams.getOptional('page');
    const [services, setServices] = useState<IService[]>([]);
    const [total, setTotal] = useState(0);
    const [isFetchingServices, setIsFetchingServices] = useState(false);
    const [nameFilter, setNameFilter] = useState('');
    const [serviceTypeFilter, setServiceTypeFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(tryParseInt(page, 1));

    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        fetchServices();
    }, [currentPage]);

    async function removeService(id: string) {
        setIsFetchingServices(true);
        await abortableFetch.deleteJson(`/api/services/${id}`);
        await fetchServices();
    }

    const fetchServices = async () => {
        setIsFetchingServices(true);
        const queryParams: Record<string, string> = { includeItemAuthorizations: 'canUpdate,canDelete' };
        if (currentPage) {
            queryParams.page = currentPage.toString();
        }
        if (nameFilter) {
            queryParams.name = nameFilter;
        }
        if (serviceTypeFilter) {
            queryParams.servicetype = serviceTypeFilter;
        }
        const params = new URLSearchParams(queryParams);
        try {
            const result = await abortableFetch.getJson(`/api/services?${params.toString()}`);
            setServices(result.json.items);
            setTotal(result.json.total);
        } finally {
            setIsFetchingServices(false);
        }
    };

    async function handleNavigation(pageNumber: number) {
        navigate(`/services?page=${pageNumber}`);
        setCurrentPage(pageNumber);
    }

    const actionButtons = hasServiceAddEditAuthorization
        ? [
              <LinkButton
                  icon="add"
                  to="/services/add"
              >
                  {translateKey('common.addItem', translateKey('service.service'))}
              </LinkButton>,
          ]
        : [];

    return (
        <FullWidthTemplate
            breadcrumb={[
                { text: translateKey('practitioner.adminSettings') },
                { text: translateKey('service.services'), url: '/services' },
            ]}
        >
            <Header
                actionButtons={actionButtons}
                content={translateKey('service.services')}
                onSearch={() => fetchServices()}
                searchFields={[
                    <HeaderSearchBox
                        onChange={(value) => setNameFilter(value)}
                        text={translateKey('common.name')}
                        value={nameFilter}
                    />,
                    <HeaderSearchSelectBox
                        onChange={(value) => setServiceTypeFilter(value)}
                        selectOptions={serviceSelectOptions}
                        value={serviceTypeFilter}
                    />,
                ]}
            />
            <AdminTable>
                <TableHead>
                    <TableRow>
                        <TableHeader>{translateKey('common.name')}</TableHeader>
                        <TableHeader>{translateKey('common.code')}</TableHeader>
                        <TableHeader>{translateKey('common.type')}</TableHeader>
                        <TableHeader>{translateKey('common.isActive')}</TableHeader>
                        <TableHeader />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {services.map((service) => (
                        <TableRow key={service.id}>
                            <TableData>
                                <Link to={`/services/${service.id}`}>{service.name} </Link>
                            </TableData>
                            <TableData>{service.code}</TableData>
                            <TableData>{translateKey('service.type_' + service.serviceType)}</TableData>
                            <TableData>
                                <NullableBoolean value={service.isActive} />
                            </TableData>
                            {service.authorization.canUpdate && (
                                <TableData align="right">
                                    <EditButton to={`/services/${service.id}/edit`} />
                                    {service.authorization.canDelete && (
                                        <DeleteButton onClick={() => removeService(service.id)} />
                                    )}
                                </TableData>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </AdminTable>
            <ListPager
                isDisabled={isFetchingServices}
                onChangePage={handleNavigation}
                page={currentPage}
                total={total}
            />
        </FullWidthTemplate>
    );
}
